import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import blockService from '../../services/blockService';
import { useDialog } from '../../context/DialogContext';
import { safeBottom } from '../../utils/safeArea';

export const REPORT_REASONS = [
  ['harassment', 'HARASSMENT'],
  ['spam', 'SPAM'],
  ['impersonation', 'PRETENDING TO BE SOMEONE'],
  ['inappropriate', 'INAPPROPRIATE'],
  ['safety', 'SAFETY'],
  ['other', 'SOMETHING ELSE'],
];

/**
 * REPORT — a sheet that works over any full-screen surface, for the viewer's Portals and
 * introductions. Sends the same POST /reports as the profile menu, and names what was on screen.
 * The person reported is never told.
 */
export default function ReportSheet({ userId, displayName, what = 'this', eventId = null, storyId = null, onClose, onBlocked = null }) {
  const { showAlert } = useDialog();
  const [reason, setReason] = useState('harassment');
  const [details, setDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    const r = await blockService.report({ userId, reason, details, eventId, storyId, alsoBlock });
    setBusy(false);
    if (!r.success) {
      showAlert({ title: 'ERROR', message: r.error || 'Could not send the report.', type: 'error' });
      return;
    }
    onClose();
    if (alsoBlock) onBlocked?.(userId);
    showAlert({ title: 'REPORT SENT', message: 'Thanks. Someone will review this, and they won’t be told who reported it.', type: 'info' });
  };

  return createPortal(
    <div
      className="pr-overlay-root"
      data-swipe-ok
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,.62)', pointerEvents: 'auto' }}
    >
      <div
        role="dialog"
        aria-label={`Report ${displayName || 'this person'}`}
        onClick={(e) => e.stopPropagation()}
        className="font-mono"
        style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 480, maxHeight: '88vh', overflowY: 'auto', boxSizing: 'border-box', padding: `22px 18px ${safeBottom(18)}`, borderRadius: '28px 28px 0 0', background: '#000', color: '#fff', boxShadow: '0 0 0 1px #262626' }}
      >
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.16em' }}>REPORT {what.toUpperCase()}</span>
        <span style={{ fontSize: 12, lineHeight: 1.5, color: '#BDBDBD' }}>What’s wrong with it? Only Portal’s team sees this.</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {REPORT_REASONS.map(([key, label]) => (
            <button key={key} type="button" aria-pressed={reason === key} onClick={() => setReason(key)}
              style={{ height: 36, padding: '0 14px', border: 0, borderRadius: 999, background: reason === key ? '#fff' : '#1F1F1F', color: reason === key ? '#000' : '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder="Anything else? (optional)"
          aria-label="Details"
          style={{ boxSizing: 'border-box', width: '100%', padding: '12px 16px', border: 0, borderRadius: 18, background: '#111', boxShadow: 'inset 0 0 0 1px #2A2A2A', color: '#fff', fontFamily: 'inherit', fontSize: 16, resize: 'none', outline: 'none' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, cursor: 'pointer' }}>
          <input type="checkbox" checked={alsoBlock} onChange={(e) => setAlsoBlock(e.target.checked)} />
          Also block {displayName || 'them'}
        </label>
        <button type="button" onClick={send} disabled={busy} style={{ height: 52, border: 0, borderRadius: 999, background: '#fff', color: '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'SENDING…' : 'SEND REPORT'}
        </button>
        <button type="button" onClick={onClose} style={{ height: 44, border: 0, borderRadius: 999, background: '#1F1F1F', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' }}>
          CANCEL
        </button>
      </div>
    </div>,
    document.body,
  );
}
